'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Subscription } from '@/core/domain';
import type { GetUserUidUseCase } from '@/features/authentication/domain/usecases/get-user-uid';
import type { GetSubscriptionByUserIdUseCase } from '../../domain/usecases/get-subscription-by-user-id';
import type { GetSubscriptionsByUserIdsUseCase } from '../../domain/usecases/get-subscriptions-by-user-ids';

export interface SubscriptionState {
  currentSubscription: Subscription | null;
  loading: boolean;
  error: string | null;
}

export interface SubscriptionEvents {
  getSubscriptionByUserId: (userId?: string) => Promise<Subscription | null>;
  /** Lista admin / enriquecimento em lote. */
  getSubscriptionsByUserIds: (userIds: string[]) => Promise<Map<string, Subscription | null>>;
  clearError: () => void;
  clearCurrentSubscription: () => void;
}

export interface SubscriptionContextValue {
  state: SubscriptionState;
  events: SubscriptionEvents;
}

export interface SubscriptionProviderProps {
  children: React.ReactNode;
  getUserUidUseCase: GetUserUidUseCase;
  getSubscriptionByUserIdUseCase: GetSubscriptionByUserIdUseCase;
  getSubscriptionsByUserIdsUseCase: GetSubscriptionsByUserIdsUseCase;
}

const initialState: SubscriptionState = {
  currentSubscription: null,
  loading: false,
  error: null,
};

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({
  children,
  getUserUidUseCase,
  getSubscriptionByUserIdUseCase,
  getSubscriptionsByUserIdsUseCase,
}: SubscriptionProviderProps) {
  const [state, setState] = useState<SubscriptionState>(initialState);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const clearCurrentSubscription = useCallback(() => {
    setState((s) => ({ ...s, currentSubscription: null }));
  }, []);

  const getSubscriptionByUserId = useCallback(
    async (explicitUserId?: string): Promise<Subscription | null> => {
      let uid = explicitUserId;
      if (uid == null || uid.length === 0) {
        uid = (await getUserUidUseCase.execute()) ?? undefined;
      }
      if (!uid) {
        setState((s) => ({
          ...s,
          error: 'Usuário não autenticado.',
          currentSubscription: null,
        }));
        return null;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const sub = await getSubscriptionByUserIdUseCase.execute(uid);
        setState((s) => ({ ...s, currentSubscription: sub, loading: false }));
        return sub;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Erro ao carregar assinatura.';
        setState((s) => ({
          ...s,
          loading: false,
          error: msg,
          currentSubscription: null,
        }));
        return null;
      }
    },
    [getUserUidUseCase, getSubscriptionByUserIdUseCase]
  );

  const getSubscriptionsByUserIds = useCallback(
    async (userIds: string[]): Promise<Map<string, Subscription | null>> => {
      return getSubscriptionsByUserIdsUseCase.execute(userIds);
    },
    [getSubscriptionsByUserIdsUseCase]
  );

  const events: SubscriptionEvents = useMemo(
    () => ({
      getSubscriptionByUserId,
      getSubscriptionsByUserIds,
      clearError,
      clearCurrentSubscription,
    }),
    [
      getSubscriptionByUserId,
      getSubscriptionsByUserIds,
      clearError,
      clearCurrentSubscription,
    ]
  );

  const value = useMemo<SubscriptionContextValue>(
    () => ({ state, events }),
    [state, events]
  );

  return (
    <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription deve ser usado dentro de SubscriptionProvider');
  }
  return ctx;
}
